// Products.jsx
import React, { useEffect, useMemo, useRef, useState } from 'react';
import api from '../api';
// import BarcodeField from '../components/BarcodeField'; // removed
import BarcodeScannerModal from '../components/BarcodeScannerModal';

// ---------- Helpers ----------
const ean13CheckDigit = (d12) => { if (!/^\d{12}$/.test(d12)) return null; let sum=0; for (let i=0;i<12;i++){ const n=d12.charCodeAt(i)-48; sum += (i%2===0)?n:n*3; } return (10-(sum%10))%10; };
const validateEAN13 = (c)=>/^\d{13}$/.test(c)&&ean13CheckDigit(c.slice(0,12))===Number(c[12]);
const validateUPCA = (c)=>/^\d{12}$/.test(c)&&ean13CheckDigit(c.slice(0,11))===Number(c[11]);
const validateCode128=(t)=>typeof t==='string'&&t.length>=1&&t.length<=80&&/^[\x20-\x7E]+$/.test(t);

const norm  = (v) => (v == null ? '' : String(v)).toLowerCase();
const money = (n) => Number(n || 0).toFixed(2);
const clamp = (n, lo, hi) => Math.min(hi, Math.max(lo, n));

// Reusable sortable th
const TH = ({ label, col, sort, onSort }) => (
  <th
    role="button"
    tabIndex={0}
    aria-sort={sort.key === col ? (sort.dir === 'asc' ? 'ascending' : 'descending') : 'none'}
    onClick={() => onSort(col)}
    onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && onSort(col)}
    style={{ cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' }}
    title="Click to sort"
  >
    {label}
    {sort.key === col && (
      <span className="mono" style={{ marginLeft: 6 }}>{sort.dir === 'asc' ? '▲' : '▼'}</span>
    )}
  </th>
);

// ---------- Slide-over Drawer ----------
function Drawer({ open, title, onClose, children, footer }) {
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  useEffect(() => {
    if (!open) return;
    const id = requestAnimationFrame(() => setMounted(true));
    return () => { cancelAnimationFrame(id); setMounted(false); };
  }, [open]);

  if (!open) return null;
  return (
    <>
      <div
        onClick={onClose}
        style={{
          position: 'fixed', inset: 0,
          background: 'rgba(0,0,0,0.35)',
          opacity: mounted ? 1 : 0,
          transition: 'opacity 160ms ease-out',
          zIndex: 1000
        }}
      />
      <aside
        role="dialog"
        aria-modal="true"
        style={{
          position: 'fixed',
          top: 0, right: 0, bottom: 0,
          width: 'min(520px, 100%)',
          background: 'var(--bg, #fff)',
          zIndex: 1001,
          display: 'flex',
          flexDirection: 'column',
          boxShadow: '0 0 0 1px var(--border, #e5e7eb), -8px 0 24px rgba(0,0,0,.15)',
          transform: mounted ? 'translateX(0)' : 'translateX(16px)',
          opacity: mounted ? 1 : 0,
          transition: 'transform 160ms ease-out, opacity 160ms ease-out'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={{ padding: '14px 16px', borderBottom: '1px solid var(--border, #e5e7eb)' }}>
          <div className="flex" style={{ alignItems:'center' }}>
            <h3 style={{ margin: 0 }}>{title}</h3>
            <button
              aria-label="Close"
              className="btn btn-sm secondary"
              onClick={onClose}
              style={{ marginLeft: 'auto' }}
            >✕</button>
          </div>
        </div>
        <div style={{ padding: 16, overflow: 'auto', flex: 1 }}>
          {children}
        </div>
        {footer && (
          <div style={{ padding: 12, borderTop: '1px solid var(--border, #e5e7eb)' }}>
            {footer}
          </div>
        )}
      </aside>
    </>
  );
}

// ---------- Info Modal ----------
function InfoModal({ open, title, content, onClose }) {
  if (!open) return null;
  return (
    <>
      <div
        onClick={onClose}
        style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(0,0,0,0.35)',
          zIndex: 2000
        }}
      />
      <div
        style={{
          position: 'fixed',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          background: 'var(--bg, #fff)',
          zIndex: 2001,
          padding: '20px',
          borderRadius: '8px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
          maxWidth: '400px',
          width: '90%',
          maxHeight: '80vh',
          overflow: 'auto'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
          <h4 style={{ margin: 0 }}>{title}</h4>
          <button
            className="btn btn-sm secondary"
            onClick={onClose}
            aria-label="Close"
          >
            ✕
          </button>
        </div>
        <div style={{ fontSize: '14px', lineHeight: '1.6', color: 'var(--text, #333)' }}>
          {content}
        </div>
      </div>
    </>
  );
}

// ---------- Basic Modal ----------
function Modal({ open, title, onClose, children, footer }) {
  if (!open) return null;
  return (
    <>
      <div
        onClick={onClose}
        style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(0,0,0,0.35)',
          zIndex: 3000
        }}
      />
      <div
        style={{
          position: 'fixed',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          background: 'var(--bg, #fff)',
          zIndex: 3001,
          padding: '16px',
          borderRadius: '8px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
          width: 'min(520px, 92vw)',
          maxHeight: '80vh',
          overflow: 'auto'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
          <h4 style={{ margin: 0 }}>{title}</h4>
          <button
            className="btn btn-sm secondary"
            onClick={onClose}
            aria-label="Close"
            style={{ marginLeft: 'auto' }}
          >
            x
          </button>
        </div>
        <div>{children}</div>
        {footer && (
          <div style={{ marginTop: 12 }}>
            {footer}
          </div>
        )}
      </div>
    </>
  );
}

// ======================================================
export default function Products() {
  const [list, setList] = useState([]);
  const [q, setQ] = useState('');
  const [scanOpen, setScanOpen] = useState(false);          // page search scanner
  const [scanFormOpen, setScanFormOpen] = useState(false);  // form barcode scanner

  const onSearchKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (debounceRef.current) clearTimeout(debounceRef.current);
      load(q.trim());
    }
  };
  const debounceRef = useRef();

  const total = list?.length || 0;
  const lowStockCount = useMemo(
    () => list.filter(p => Number(p.stock || 0) <= Number(p.low_stock_threshold || 0)).length,
    [list]
  );

  const [sort, setSort] = useState({ key: 'name', dir: 'asc' });
  const toggleSort = (key) =>
    setSort((s) => ({ key, dir: s.key === key && s.dir === 'asc' ? 'desc' : 'asc' }));

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');
  const [infoOpen, setInfoOpen] = useState(null); // Track which info modal is open
  const [form, setForm] = useState({
    name: '', sku: '', barcode: '',
    price: '', gst_rate: '', discount_pct: '',
    stock: '', low_stock_threshold: '',
    category_id: ''
  });
  const [errors, setErrors] = useState({});
  const [categories, setCategories] = useState([]);
  const [categoryFilter, setCategoryFilter] = useState('');
  const [catOpen, setCatOpen] = useState(false);
  const [catBusy, setCatBusy] = useState(false);
  const [catErr, setCatErr] = useState('');
  const [catNew, setCatNew] = useState('');
  const [catEditId, setCatEditId] = useState(null);
  const [catEditName, setCatEditName] = useState('');
  const validateBarcode = (code) => {
    if (!code) return null;
    if (/^\d{12}$/.test(code)) return validateUPCA(code) ? null : 'Invalid UPC-A (check digit failed)';
    if (/^\d{13}$/.test(code)) return validateEAN13(code) ? null : 'Invalid EAN-13 (check digit failed)';
    return validateCode128(code) ? null : 'Invalid characters for Code-128 / text';
  };

  const fileRef = useRef();
  const [csvFile, setCsvFile] = useState(null); // <-- holds selected CSV
  const uncategorizedId = useMemo(() => {
    const unc = categories.find(c => String(c.name || '').toLowerCase() === 'uncategorized');
    return unc?.id || categories[0]?.id || 1;
  }, [categories]);

  async function loadCategories() {
    try {
      const res = await api.get('/product-categories');
      setCategories(res.data || []);
    } catch (e) {
      console.warn('Failed to load categories:', e?.message || e);
    }
  }

  async function load(search = '') {
    const s = String(search || '').trim();
    try {
      if (s) {
        // Prefer search endpoint (supports name, SKU, barcode)
        const res = await api.get('/products/search', { params: { q: s, limit: 20000 } });
        setList(res.data || []);
      } else {
        const res = await api.get('/products');
        setList(res.data || []);
      }
    } catch (err) {
      // Fallback to legacy /products?q=... if /products/search isn't available
      console.warn('Falling back to /products due to error:', err?.response?.status, err?.message);
      const params = s ? { params: { q: s } } : undefined;
      const res = await api.get('/products', params);
      setList(res.data || []);
    }
  }
  useEffect(() => { load(); }, []);
  useEffect(() => { loadCategories(); }, []);
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => load(q.trim()), 250);
    return () => debounceRef.current && clearTimeout(debounceRef.current);
  }, [q]);
  useEffect(() => {
    if (!form.category_id && categories.length) {
      setForm(f => ({ ...f, category_id: String(uncategorizedId) }));
    }
  }, [categories, uncategorizedId, form.category_id]);

  const sorted = useMemo(() => {
    const arr = Array.isArray(list) ? [...list] : [];
    const filtered = categoryFilter
      ? arr.filter(p => String(p.category_id || '') === String(categoryFilter))
      : arr;
    const { key, dir } = sort;

    const val = (p) => {
      switch (key) {
        case 'name': return norm(p.name);
        case 'sku': return norm(p.sku);
        case 'category': return norm(p.category_name || '');
        case 'price': return Number(p.price || 0);
        case 'gst_rate': return Number(p.gst_rate || 0);
        case 'discount_pct': return Number(p.discount_pct || 0);
        case 'stock': return Number(p.stock || 0);
        case 'low_stock_threshold': return Number(p.low_stock_threshold || 0);
        default: return norm(p.name);
      }
    };

    filtered.sort((a, b) => {
      const va = val(a), vb = val(b);
      let cmp = va < vb ? -1 : va > vb ? 1 : 0;
      if (cmp === 0) {
        const na = norm(a.name), nb = norm(b.name);
        cmp = na < nb ? -1 : na > nb ? 1 : (Number(a.id || 0) - Number(b.id || 0));
      }
      return dir === 'asc' ? cmp : -cmp;
    });

    return filtered;
  }, [list, sort, categoryFilter]);

  const dupSku = useMemo(() => {
    const s = norm(form.sku);
    if (!s) return false;
    return (list || []).some(p => norm(p.sku) === s && (!editId || p.id !== editId));
  }, [form.sku, list, editId]);

  function onChange(e) {
    const { name, value } = e.target;
    // For stock and low_stock_threshold, only allow whole numbers
    if ((name === 'stock' || name === 'low_stock_threshold') && value !== '') {
      const num = parseFloat(value);
      if (isNaN(num)) return; // Reject non-numeric input
      // Check if it contains a decimal point
      if (value.includes('.')) {
        return; // Reject decimal values
      }
    }
    setForm(f => ({ ...f, [name]: value }));
  }

  function openNew() {
    setEditId(null);
    setErr('');
    setForm({
      name:'', sku:'', barcode:'',
      price:'', gst_rate:'', discount_pct:'',
      stock:'', low_stock_threshold:'',
      category_id: String(uncategorizedId)
    });
    setDrawerOpen(true);
  }
  function openEdit(p) {
    setEditId(p.id);
    setErr('');
    setForm({
      name: p.name || '',
      sku: p.sku || '',
      barcode: p.barcode || '',
      price: (p.price ?? '') === '' ? '' : String(p.price),
      gst_rate: (p.gst_rate ?? '') === '' ? '' : String(p.gst_rate),
      discount_pct: (p.discount_pct ?? '') === '' ? '' : String(p.discount_pct),
      stock: (p.stock ?? '') === '' ? '' : String(p.stock),
      low_stock_threshold: (p.low_stock_threshold ?? '') === '' ? '' : String(p.low_stock_threshold),
      category_id: String(p.category_id ?? uncategorizedId),
    });
    setDrawerOpen(true);
  }
  function closeDrawer() { setDrawerOpen(false); }

  async function onSubmit(e) {
    e.preventDefault();
    if (saving) return;

    setErr('');
    const payload = {
      ...form,
      price: Number(form.price || 0),
      gst_rate: Number(form.gst_rate || 0),
      discount_pct: clamp(Number(form.discount_pct || 0), 0, 100),
      stock: Number(form.stock || 0),
      low_stock_threshold: Number(form.low_stock_threshold || 0),
      category_id: Number(form.category_id || uncategorizedId),
    };

    if (!form.name || !form.sku) {
      setErr('Name and SKU are required.');
      return;
    }
    if (dupSku) {
      setErr('A product with this SKU already exists.');
      return;
    }
    try {
      setSaving(true);
      if (editId) await api.put('/products/' + editId, payload);
      else await api.post('/products', payload);
      closeDrawer();
      await load(q.trim());
    } catch (e0) {
      setErr(e0?.response?.data?.message || e0?.response?.data?.error || 'Save failed');
    } finally {
      setSaving(false);
    }
  }

  async function onDelete(id) {
    if (!window.confirm('Delete this product?')) return;
    await api.delete('/products/' + id);
    await load(q.trim());
    if (editId === id) closeDrawer();
  }

  // CSV import/export
  async function importCSVFile(file) {
    const fd = new FormData();
    fd.append('file', file);
    try {
      const res = await api.post('/products/import', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      const r = res.data || {};
      alert(`Imported: processed ${r.processed || 0} and updated ${r.upserted || 0}${(r.errors && r.errors.length) ? `, errors: ${r.errors.length}` : ''}`);
      await load(q.trim());
    } catch (e1) {
      alert('Import failed: ' + (e1?.response?.data?.error || e1.message));
    } finally {
      if (fileRef.current) fileRef.current.value = '';
    }
  }
  function onPickCSV() { fileRef.current?.click(); }
  function onFileChange(e) {
    const f = e.target.files?.[0];
    setCsvFile(f || null); // select only; no auto-upload
  }
  async function onUploadCSV() {
    if (!csvFile) return;
    await importCSVFile(csvFile);
    setCsvFile(null);
    if (fileRef.current) fileRef.current.value = '';
  }
  function clearCSV() {
    setCsvFile(null);
    if (fileRef.current) fileRef.current.value = '';
  }

  function buildCSV(rows) {
    const headers = ['name','sku','category','barcode','price','gst_rate','discount_pct','stock','low_stock_threshold'];
    const esc = (s) => {
      const str = String(s ?? '');
      return /[",\n\r]/.test(str) ? `"${str.replace(/"/g,'""')}"` : str;
    };
    const lines = [
      headers.join(','),
      ...rows.map(r => [
        r.name ?? '',
        r.sku ?? '',
        r.category_name ?? r.category ?? '',
        r.barcode ?? '',
        (r.price ?? 0),
        (r.gst_rate ?? 0),
        (r.discount_pct ?? 0),
        (r.stock ?? 0),
        (r.low_stock_threshold ?? 0)
      ].map(esc).join(','))
    ];
    return lines.join('\n');
  }
  function downloadCSV(name, rows) {
    const csv = buildCSV(rows);
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = name; a.click();
    URL.revokeObjectURL(url);
  }
  function exportFiltered() {
    downloadCSV(`products_${Date.now()}.csv`, sorted);
  }

  async function createCategory() {
    const name = String(catNew || '').trim();
    if (!name) return;
    try {
      setCatBusy(true);
      setCatErr('');
      await api.post('/product-categories', { name });
      setCatNew('');
      await loadCategories();
      await load(q.trim());
    } catch (e) {
      setCatErr(e?.response?.data?.error || 'Failed to create category');
    } finally {
      setCatBusy(false);
    }
  }

  async function saveCategory() {
    const name = String(catEditName || '').trim();
    if (!catEditId || !name) return;
    try {
      setCatBusy(true);
      setCatErr('');
      await api.put('/product-categories/' + catEditId, { name });
      setCatEditId(null);
      setCatEditName('');
      await loadCategories();
      await load(q.trim());
    } catch (e) {
      setCatErr(e?.response?.data?.error || 'Failed to update category');
    } finally {
      setCatBusy(false);
    }
  }

  async function deleteCategory(id) {
    if (!window.confirm('Delete this category? Products will be moved to Uncategorized.')) return;
    try {
      setCatBusy(true);
      setCatErr('');
      await api.delete('/product-categories/' + id);
      if (String(categoryFilter) === String(id)) setCategoryFilter('');
      if (String(form.category_id) === String(id)) {
        setForm(f => ({ ...f, category_id: String(uncategorizedId) }));
      }
      await loadCategories();
      await load(q.trim());
    } catch (e) {
      setCatErr(e?.response?.data?.error || 'Failed to delete category');
    } finally {
      setCatBusy(false);
    }
  }

  // pagination
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(() => {
    const v = Number(localStorage.getItem('products.rowsPerPage'));
    return [10, 25, 50, 100].includes(v) ? v : 25;
  });
  useEffect(() => { setPage(1); }, [q, categoryFilter]);
  const pageCount = useMemo(
    () => Math.max(1, Math.ceil((sorted?.length || 0) / rowsPerPage)),
    [sorted, rowsPerPage]
  );
  useEffect(() => { if (page > pageCount) setPage(pageCount); }, [pageCount]);
  const onChangeRowsPerPage = (e) => {
    const v = Number(e.target.value || 25);
    localStorage.setItem('products.rowsPerPage', String(v));
    setRowsPerPage(v);
    setPage(1);
  };
  const startIdx = (page - 1) * rowsPerPage;
  const endIdx = startIdx + rowsPerPage;
  const pageRows = useMemo(() => sorted.slice(startIdx, endIdx), [sorted, startIdx, endIdx]);
  const gotoFirst = () => setPage(1);
  const gotoPrev  = () => setPage(p => Math.max(1, p - 1));
  const gotoNext  = () => setPage(p => Math.min(pageCount, p + 1));
  const gotoLast  = () => setPage(pageCount);

  // UI
  return (
    <div className="container">
      <div style={{ display:'flex', gap:12, alignItems:'center', marginBottom:8, flexWrap:'wrap' }}>
        <h2 style={{ marginRight:'auto' }}>Products</h2>
        <div className="muted" style={{ whiteSpace:'nowrap' }}>
          Total: <b>{total}</b> • Low stock: <b>{lowStockCount}</b>
        </div>
      </div>

      <div style={{ display:'flex', gap:8, margin:'8px 0 12px 0', alignItems:'center', flexWrap:'wrap' }}>
        <input
          className="input"
          placeholder="Search by name, SKU, or barcode"
          style={{ flex:1, minWidth:260 }}
          value={q}
          onChange={(e)=>setQ(e.target.value)}
          onKeyDown={onSearchKeyDown}
        />
        <select
          className="input"
          value={categoryFilter}
          onChange={(e) => setCategoryFilter(e.target.value)}
          style={{ minWidth: 200 }}
          aria-label="Filter by category"
        >
          <option value="">All categories</option>
          {categories.map(c => (
            <option key={c.id} value={c.id}>{c.name}</option>
          ))}
        </select>
        <button className="btn secondary" onClick={() => setScanOpen(true)}>Scan</button>
        <button
          type="button"
          className="btn btn-sm secondary"
          onClick={() => setInfoOpen('page_scan')}
          style={{ padding: '2px 6px', fontSize: '12px', lineHeight: '1' }}
          title="Learn more about Scan"
        >
          ℹ️
        </button>
        {q ? <button className="btn secondary" onClick={()=>setQ('')}>Clear</button> : null}
        <button className="btn secondary" onClick={() => setCatOpen(true)}>Categories</button>
        <button
          type="button"
          className="btn btn-sm secondary"
          onClick={() => setInfoOpen('categories')}
          style={{ padding: '2px 6px', fontSize: '12px', lineHeight: '1' }}
          title="Learn more about Categories"
        >
          ℹ️
        </button>
        <button className="btn" onClick={openNew}>+ Add</button>
      </div>

      <div style={{ display:'flex', gap:8, margin:'8px 0 12px 0', alignItems:'center', flexWrap:'wrap' }}>
        {/* CSV controls */}
        <input
          type="file"
          accept=".csv,text/csv"
          ref={fileRef}
          style={{ display:'none' }}
          onChange={onFileChange}
        />
        <button className="btn secondary" onClick={onPickCSV} title="Select CSV file to upload">Choose CSV</button>
        <button
          type="button"
          className="btn btn-sm secondary"
          onClick={() => setInfoOpen('choose_csv')}
          style={{ padding: '4px 8px', fontSize: '12px', lineHeight: '1' }}
          title="Learn more about Choose CSV"
        >
          ℹ️
        </button>
        {csvFile
          ? (
            <span
              className="muted mono"
              style={{ maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
              title={csvFile.name}
            >
              {csvFile.name}
            </span>
          )
          : <span className="muted">No file selected</span>
        }
        <button className="btn secondary" onClick={onUploadCSV} disabled={!csvFile} title="Upload selected CSV file">Upload CSV</button>
        <button
          type="button"
          className="btn btn-sm secondary"
          onClick={() => setInfoOpen('upload_csv')}
          style={{ padding: '4px 8px', fontSize: '12px', lineHeight: '1' }}
          title="Learn more about Upload CSV"
        >
          ℹ️
        </button>
        <button className="btn secondary" onClick={clearCSV} disabled={!csvFile}>Clear</button>

        <a className="btn link" href="/sample_products.csv" download title="Download sample CSV file">Sample CSV</a>
        <button
          type="button"
          className="btn btn-sm secondary"
          onClick={() => setInfoOpen('sample_csv')}
          style={{ padding: '4px 8px', fontSize: '12px', lineHeight: '1' }}
          title="Learn more about Sample CSV"
        >
          ℹ️
        </button>
        <button className="btn secondary" onClick={exportFiltered} title="Export current product list as CSV">Export CSV</button>
        <button
          type="button"
          className="btn btn-sm secondary"
          onClick={() => setInfoOpen('export_csv')}
          style={{ padding: '4px 8px', fontSize: '12px', lineHeight: '1' }}
          title="Learn more about Export CSV"
        >
          ℹ️
        </button>
      </div>

      {scanOpen && (
        <BarcodeScannerModal
          onClose={() => setScanOpen(false)}
          onDetected={(code) => {
            const v=(code||'').trim();
            setQ(v);
            setScanOpen(false);
            if (debounceRef.current) clearTimeout(debounceRef.current);
            load(v);
          }}
        />
      )}

      <div className="card">
        <div className="flex" style={{ alignItems:'center', marginBottom:6 }}>
          <h3 style={{ margin:0 }}>List</h3>
        </div>

        <div style={{ overflowX:'auto' }}>
          <table className="table">
            <thead>
              <tr>
                <TH label="Name"      col="name"      sort={sort} onSort={toggleSort} />
                <TH label="SKU"       col="sku"       sort={sort} onSort={toggleSort} />
                <TH label="Category"  col="category"  sort={sort} onSort={toggleSort} />
                <TH label="Price"     col="price"     sort={sort} onSort={toggleSort} />
                <TH label="GST%"      col="gst_rate"  sort={sort} onSort={toggleSort} />
                <TH label="Disc%"     col="discount_pct" sort={sort} onSort={toggleSort} />
                <TH label="Stock"     col="stock"     sort={sort} onSort={toggleSort} />
                <TH label="LowStock"  col="low_stock_threshold" sort={sort} onSort={toggleSort} />
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {pageRows.map(p => {
                const low = Number(p.stock || 0) <= Number(p.low_stock_threshold || 0);
                return (
                  <tr key={p.id} style={low ? { background:'var(--warn-bg, #fffbea)' } : undefined}>
                    <td>{p.name}</td>
                    <td className="mono">{p.sku}</td>
                    <td>{p.category_name || 'Uncategorized'}</td>
                    <td>₹ {money(p.price)}</td>
                    <td>{Number(p.gst_rate || 0).toFixed(2)}</td>
                    <td>{Number(p.discount_pct || 0).toFixed(2)}</td>
                    <td>{Number(p.stock || 0)}</td>
                    <td>{Number(p.low_stock_threshold || 0)}</td>
                    <td style={{ whiteSpace:'nowrap' }}>
                      <button className="btn btn-sm" onClick={() => openEdit(p)}>Edit</button>
                      <button className="btn btn-sm btn-danger" onClick={() => onDelete(p.id)} style={{ marginLeft:8 }}>
                        Delete
                      </button>
                    </td>
                  </tr>
                );
              })}
              {!sorted.length && (
                <tr><td colSpan={9} style={{ textAlign:'center', padding:16 }}>No products</td></tr>
              )}
            </tbody>
          </table>
        </div>

        <div className="flex" style={{
          alignItems: 'center',
          gap: 12,
          justifyContent: 'space-between',
          padding: '8px 12px',
          borderTop: '1px solid var(--border, #e5e7eb)',
          flexWrap: 'wrap'
        }}>
          <div className="flex" style={{ alignItems:'center', gap:8 }}>
            <label className="muted" htmlFor="rows-per-page">Rows/page</label>
            <select
              id="rows-per-page"
              className="input"
              value={rowsPerPage}
              onChange={onChangeRowsPerPage}
              style={{ width: 96 }}
              aria-label="Rows per page"
            >
              {[10,25,50,100].map(n => <option key={n} value={n}>{n}</option>)}
            </select>

            <span className="muted" style={{ marginLeft: 12 }}>
              {sorted.length
                ? `${startIdx + 1}–${Math.min(endIdx, sorted.length)} of ${sorted.length}`
                : '0 of 0'}
            </span>
          </div>

          <div className="flex" style={{ alignItems:'center', gap:6 }}>
            <button className="btn btn-sm secondary" onClick={gotoFirst} disabled={page <= 1} aria-label="First page">«</button>
            <button className="btn btn-sm secondary" onClick={gotoPrev}  disabled={page <= 1} aria-label="Previous page">‹</button>
            <span className="muted" style={{ padding: '0 6px' }}>
              Page <b>{page}</b> / {pageCount}
            </span>
            <button className="btn btn-sm secondary" onClick={gotoNext} disabled={page >= pageCount} aria-label="Next page">›</button>
            <button className="btn btn-sm secondary" onClick={gotoLast} disabled={page >= pageCount} aria-label="Last page">»</button>
          </div>
        </div>
      </div>

      <Drawer
        open={drawerOpen}
        onClose={closeDrawer}
        title={editId ? 'Edit Product' : 'Add Product'}
        footer={
          <div className="flex" style={{ gap:8, justifyContent:'flex-end' }}>
            {editId && (
              <button className="btn btn-danger" type="button" onClick={()=>onDelete(editId)}>
                Delete
              </button>
            )}
            <button className="btn btn-secondary" type="button" onClick={closeDrawer}>Cancel</button>
            <button className="btn" form="product-form" type="submit" disabled={saving || !form.name || !form.sku || dupSku}>
              {saving ? 'Saving…' : (editId ? 'Update' : 'Save')}
            </button>
          </div>
        }
      >
        {err && <div className="alert alert-error" style={{ marginBottom:8 }}>{err}</div>}

        <form id="product-form" onSubmit={onSubmit} style={{ display:'grid', gap:10 }}>
          <div>
            <label className="muted" style={{ fontSize:12 }}>Name *</label>
            <input className="input" name="name" placeholder="Product name"
                   value={form.name} onChange={onChange} required />
          </div>

          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '4px' }}>
              <label className="muted" style={{ fontSize:12 }}>Category</label>
              <button
                type="button"
                className="btn btn-sm secondary"
                onClick={() => setCatOpen(true)}
                style={{ padding: '2px 6px', fontSize: '12px', lineHeight: '1' }}
                title="Manage categories"
              >
                Manage
              </button>
            </div>
            <select
              className="input"
              name="category_id"
              value={form.category_id}
              onChange={onChange}
            >
              {categories.length === 0 && (
                <option value={uncategorizedId}>Uncategorized</option>
              )}
              {categories.map(c => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
          </div>

          <div className="auto-grid-2">
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '4px' }}>
                <label className="muted" style={{ fontSize:12 }}>SKU *</label>
                <button
                  type="button"
                  className="btn btn-sm secondary"
                  onClick={() => setInfoOpen('sku')}
                  style={{ padding: '2px 6px', fontSize: '12px', lineHeight: '1' }}
                  title="Learn more about SKU"
                >
                  ℹ️
                </button>
              </div>
              <input className="input" name="sku" placeholder="Unique SKU"
                     value={form.sku} onChange={onChange} required />
              {dupSku && <div className="muted" style={{ color:'var(--danger)', fontSize:12, marginTop:4 }}>
                Duplicate SKU found
              </div>}
            </div>

            {/* Barcode field */}
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '4px' }}>
                <label className="muted" style={{ fontSize:12 }}>Barcode</label>
                <button
                  type="button"
                  className="btn btn-sm secondary"
                  onClick={() => setInfoOpen('barcode')}
                  style={{ padding: '2px 6px', fontSize: '12px', lineHeight: '1' }}
                  title="Learn more about Barcode"
                >
                  ℹ️
                </button>
              </div>
              <input
                className="input"
                name="barcode"
                placeholder="Optional"
                value={form.barcode}
                onChange={(e)=> setForm(s=>({...s, barcode: e.target.value}))}
              />
              <div style={{ display:'flex', gap:8, alignItems:'center', marginTop: 8 }}>
                <button
                  type="button"
                  className="btn secondary"
                  onClick={() => setForm(s => ({ ...s, barcode: '' }))}
                  title="Clear barcode"
                  style={{ flex: 1 }}
                >
                  Clear
                </button>
                <button
                  type="button"
                  className="btn"
                  onClick={() => setScanFormOpen(true)}
                  title="Scan barcode"
                  style={{ flex: 1 }}
                >
                  Scan
                </button>
                <button
                  type="button"
                  className="btn btn-sm secondary"
                  onClick={() => setInfoOpen('barcode_scan')}
                  style={{ padding: '2px 6px', fontSize: '12px', lineHeight: '1' }}
                  title="Learn more about Scan"
                >
                  ℹ️
                </button>
              </div>
            </div>
          </div>

          {/* Price / GST / Discount / Stock */}
          <div className="auto-grid-4">
            <div>
              <label className="muted" style={{ fontSize:12 }}>Price (₹)</label>
              <input className="input mono" type="number" step="1" name="price" placeholder="0.00"
                     value={form.price} onChange={onChange} />
            </div>
            <div>
              <label className="muted" style={{ fontSize:12 }}>GST %</label>
              <input className="input mono" type="number" step="0.01" min="0" max="100" name="gst_rate" placeholder="0.00"
                     value={form.gst_rate} onChange={onChange} />
            </div>
            <div>
              <label className="muted" style={{ fontSize:12 }}>Discount %</label>
              <input className="input mono" type="number" step="0.01" min="0" max="100" name="discount_pct" placeholder="0.00"
                     value={form.discount_pct} onChange={onChange} />
            </div>
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '4px' }}>
                <label className="muted" style={{ fontSize:12 }}>Stock</label>
                <button
                  type="button"
                  className="btn btn-sm secondary"
                  onClick={() => setInfoOpen('stock')}
                  style={{ padding: '2px 6px', fontSize: '12px', lineHeight: '1' }}
                  title="Learn more about Stock"
                >
                  ℹ️
                </button>
              </div>
              <input className="input mono" type="number" step="1" min="0" name="stock" placeholder="0"
                     value={form.stock} onChange={onChange} />
            </div>
          </div>

          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '4px' }}>
              <label className="muted" style={{ fontSize:12 }}>Low stock threshold</label>
              <button
                type="button"
                className="btn btn-sm secondary"
                onClick={() => setInfoOpen('low_stock_threshold')}
                style={{ padding: '2px 6px', fontSize: '12px', lineHeight: '1' }}
                title="Learn more about Low stock threshold"
              >
                ℹ️
              </button>
            </div>
            <input className="input mono" type="number" step="1" min="0" name="low_stock_threshold" placeholder="0"
                   value={form.low_stock_threshold} onChange={onChange} />
          </div>
        </form>
      </Drawer>

      <Modal
        open={catOpen}
        onClose={() => {
          setCatOpen(false);
          setCatErr('');
          setCatEditId(null);
          setCatEditName('');
        }}
        title="Manage Categories"
      >
        {catErr && <div className="alert alert-error" style={{ marginBottom: 8 }}>{catErr}</div>}

        <div className="flex" style={{ gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
          <input
            className="input"
            placeholder="New category name"
            value={catNew}
            onChange={(e) => setCatNew(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') createCategory(); }}
            style={{ flex: 1, minWidth: 220 }}
          />
          <button className="btn" onClick={createCategory} disabled={catBusy || !catNew.trim()}>
            Add
          </button>
        </div>

        <div style={{ display: 'grid', gap: 8 }}>
          {categories.map((c) => {
            const isDefault = String(c.name || '').toLowerCase() === 'uncategorized' || String(c.id) === String(uncategorizedId);
            const isEditing = catEditId === c.id;
            return (
              <div key={c.id} className="card" style={{ padding: 10 }}>
                <div className="flex" style={{ alignItems: 'center', gap: 8 }}>
                  {isEditing ? (
                    <input
                      className="input"
                      value={catEditName}
                      onChange={(e) => setCatEditName(e.target.value)}
                      style={{ flex: 1 }}
                    />
                  ) : (
                    <div style={{ flex: 1, fontWeight: 600 }}>
                      {c.name}
                      {isDefault ? <span className="muted" style={{ marginLeft: 8 }}>(default)</span> : null}
                    </div>
                  )}
                  {isEditing ? (
                    <>
                      <button className="btn btn-sm" onClick={saveCategory} disabled={catBusy || !catEditName.trim()}>
                        Save
                      </button>
                      <button
                        className="btn btn-sm secondary"
                        onClick={() => { setCatEditId(null); setCatEditName(''); }}
                        disabled={catBusy}
                      >
                        Cancel
                      </button>
                    </>
                  ) : (
                    <>
                      <button
                        className="btn btn-sm secondary"
                        onClick={() => { setCatEditId(c.id); setCatEditName(c.name || ''); }}
                        disabled={catBusy || isDefault}
                        title={isDefault ? 'Default category cannot be edited' : 'Edit category'}
                      >
                        Edit
                      </button>
                      <button
                        className="btn btn-sm btn-danger"
                        onClick={() => deleteCategory(c.id)}
                        disabled={catBusy || isDefault}
                        title={isDefault ? 'Default category cannot be deleted' : 'Delete category'}
                      >
                        Delete
                      </button>
                    </>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </Modal>

      {/* Form scanner modal (fills barcode field) */}
      {scanFormOpen && (
        <BarcodeScannerModal
          onClose={() => setScanFormOpen(false)}
          onDetected={(code) => {
            const v = (code || '').trim();
            setForm(s => ({ ...s, barcode: v }));
            setScanFormOpen(false);
          }}
        />
      )}

      {/* Info Modals for Product Form */}
      <InfoModal
        open={infoOpen === 'sku'}
        title="SKU (Stock Keeping Unit)"
        onClose={() => setInfoOpen(null)}
        content={
          <div>
            <p><strong>SKU</strong> stands for Stock Keeping Unit. It's a unique identifier assigned to each product.</p>
            <p><strong>Key Points:</strong></p>
            <ul style={{ marginTop: '8px', marginBottom: '8px', paddingLeft: '20px' }}>
              <li>Must be unique - no two products can have the same SKU</li>
              <li>Can contain letters and numbers (e.g., PROD-001, ABC123)</li>
              <li>Used for inventory tracking and ordering</li>
              <li>Helps organize and search products efficiently</li>
            </ul>
            <p><strong>Example:</strong> SHIRT-BLUE-L, BOOK-ISBN-001, or WIDGET-S</p>
          </div>
        }
      />

      <InfoModal
        open={infoOpen === 'barcode'}
        title="Barcode"
        onClose={() => setInfoOpen(null)}
        content={
          <div>
            <p><strong>Barcode</strong> is a machine-readable code used to quickly identify products at checkout or inventory management.</p>
            <p><strong>Supported Formats:</strong></p>
            <ul style={{ marginTop: '8px', marginBottom: '8px', paddingLeft: '20px' }}>
              <li><strong>EAN-13:</strong> 13-digit barcode (most common in India and Europe)</li>
              <li><strong>UPC-A:</strong> 12-digit barcode (commonly used in North America)</li>
              <li><strong>Code-128:</strong> Text-based barcode (flexible format)</li>
            </ul>
            <p><strong>Optional:</strong> You can leave this empty if the product doesn't have a barcode.</p>
            <p><strong>Tip:</strong> Use the Scan button to automatically capture barcodes using your device's camera.</p>
          </div>
        }
      />

      <InfoModal
        open={infoOpen === 'barcode_scan'}
        title="Barcode Scanner"
        onClose={() => setInfoOpen(null)}
        content={
          <div>
            <p><strong>Scan</strong> button allows you to capture product barcodes using your device's camera or barcode scanner device.</p>
            <p><strong>How it works:</strong></p>
            <ul style={{ marginTop: '8px', marginBottom: '8px', paddingLeft: '20px' }}>
              <li>Click the Scan button</li>
              <li>Point your camera at the barcode</li>
              <li>The barcode will be automatically read and filled in</li>
            </ul>
            <p><strong>Supported codes:</strong> EAN-13, UPC-A, Code-128, and more standard formats.</p>
            <p><strong>Tip:</strong> Works best in good lighting conditions with clear, undamaged barcodes.</p>
          </div>
        }
      />

      <InfoModal
        open={infoOpen === 'page_scan'}
        title="Product Search Scanner"
        onClose={() => setInfoOpen(null)}
        content={
          <div>
            <p><strong>Scan</strong> button in the search bar allows you to quickly search for products using barcode scanning.</p>
            <p><strong>How it works:</strong></p>
            <ul style={{ marginTop: '8px', marginBottom: '8px', paddingLeft: '20px' }}>
              <li>Click the Scan button at the top</li>
              <li>Point your camera at a product barcode</li>
              <li>The barcode will be automatically scanned and the product will be searched</li>
              <li>Results will automatically appear below</li>
            </ul>
            <p><strong>Use cases:</strong> Quickly find a specific product by its barcode without typing the SKU or name.</p>
            <p><strong>Supported codes:</strong> EAN-13, UPC-A, Code-128, and more standard formats.</p>
            <p><strong>Tip:</strong> Works best in good lighting with clear barcodes.</p>
          </div>
        }
      />

      <InfoModal
        open={infoOpen === 'categories'}
        title="Categories"
        onClose={() => setInfoOpen(null)}
        content={
          <div>
            <p><strong>Categories</strong> help you group and filter products.</p>
            <p><strong>How to use:</strong></p>
            <ul style={{ marginTop: '8px', marginBottom: '8px', paddingLeft: '20px' }}>
              <li>Create categories for product types (e.g., Electronics, Apparel)</li>
              <li>Assign a category to each product from the product form</li>
              <li>Use the category filter to quickly find items</li>
            </ul>
            <p><strong>Tip:</strong> Keep category names short and consistent for easier search.</p>
          </div>
        }
      />

      <InfoModal
        open={infoOpen === 'stock'}
        title="Stock"
        onClose={() => setInfoOpen(null)}
        content={
          <div>
            <p><strong>Stock</strong> represents the current quantity of the product available in your inventory.</p>
            <p><strong>Key Information:</strong></p>
            <ul style={{ marginTop: '8px', marginBottom: '8px', paddingLeft: '20px' }}>
              <li>Only whole numbers allowed (e.g., 10, 50, 100)</li>
              <li>Automatically decreases when customers purchase</li>
              <li>Track your inventory levels in real-time</li>
              <li>Used to calculate available stock for orders</li>
            </ul>
            <p><strong>Example:</strong> If you have 50 units in stock and sell 5, your stock becomes 45.</p>
            <p><strong>Tip:</strong> Monitor stock levels to avoid overselling and plan restocking.</p>
          </div>
        }
      />

      <InfoModal
        open={infoOpen === 'low_stock_threshold'}
        title="Low Stock Threshold"
        onClose={() => setInfoOpen(null)}
        content={
          <div>
            <p><strong>Low Stock Threshold</strong> is the minimum quantity level below which a product is considered low on stock.</p>
            <p><strong>How it works:</strong></p>
            <ul style={{ marginTop: '8px', marginBottom: '8px', paddingLeft: '20px' }}>
              <li>When stock ≤ threshold, the product is flagged as low stock</li>
              <li>Highlighted in the product list for easy identification</li>
              <li>Helps with inventory planning and reordering</li>
              <li>Only whole numbers allowed</li>
            </ul>
            <p><strong>Example:</strong> If threshold is 10 and stock is 8, the product shows as low stock.</p>
            <p><strong>Tip:</strong> Set appropriate thresholds based on your sales rate and supplier lead time.</p>
          </div>
        }
      />

      {/* Info Modals for CSV Controls */}
      <InfoModal
        open={infoOpen === 'choose_csv'}
        title="Choose CSV"
        onClose={() => setInfoOpen(null)}
        content={
          <div>
            <p><strong>Choose CSV</strong> allows you to select a CSV (Comma-Separated Values) file from your computer.</p>
            <p><strong>How it works:</strong></p>
            <ul style={{ marginTop: '8px', marginBottom: '8px', paddingLeft: '20px' }}>
              <li>Click "Choose CSV" to browse your files</li>
              <li>Select a valid CSV file with product data</li>
              <li>The file name will appear on screen</li>
              <li>Then click "Upload CSV" to import the data</li>
            </ul>
            <p><strong>Supported Format:</strong> Standard CSV with columns like name, sku, price, stock, etc.</p>
            <p><strong>Tip:</strong> Download the Sample CSV to see the correct format before preparing your own file.</p>
          </div>
        }
      />


      <InfoModal
        open={infoOpen === 'upload_csv'}
        title="Upload CSV"
        onClose={() => setInfoOpen(null)}
        content={
          <div>
            <p>
              <strong>Upload CSV</strong> lets you import or update products in bulk using a CSV file.
            </p>
        
            <p><strong>What happens:</strong></p>
            <ul style={{ marginTop: '8px', marginBottom: '8px', paddingLeft: '20px' }}>
              <li>New products from the CSV are created automatically</li>
              <li>Existing products with matching SKUs are updated</li>
              <li>All rows are validated before saving</li>
              <li>A summary shows created, updated, and error counts</li>
            </ul>
        
            <p><strong>Stock quantity handling:</strong></p>
            <ul style={{ marginTop: '8px', marginBottom: '8px', paddingLeft: '20px' }}>
              <li>
                If the stock value in CSV is a <strong>number</strong> (example: <code>50</code>),
                it will <strong>set the stock to that value</strong>.
              </li>
              <li>
                If the stock value starts with <strong>+</strong> (example: <code>+10</code>),
                it will <strong>increase existing stock</strong>.
              </li>
              <li>
                If the stock value starts with <strong>-</strong> (example: <code>-5</code>),
                it will <strong>decrease existing stock</strong>.
              </li>
            </ul>
        
            <p>
              <strong>Example:</strong>  
              If current stock is <strong>20</strong> and CSV has <code>10</code>,
              final stock becomes <strong>30</strong>.
              If current stock is <strong>20</strong> and CSV has <code>-10</code>,
              final stock becomes <strong>10</strong>.
            </p>
        
            <p><strong>Error Handling:</strong>  
              Any invalid rows are skipped and shown in the error report so you can fix and re-upload.
            </p>
        
            <p><strong>Tip:</strong>  
              Always take a backup before importing large CSV files.
            </p>
          </div>
        }
      />


      <InfoModal
        open={infoOpen === 'sample_csv'}
        title="Sample CSV"
        onClose={() => setInfoOpen(null)}
        content={
          <div>
            <p><strong>Sample CSV</strong> is a pre-formatted CSV file showing the correct structure for importing products.</p>
            <p><strong>What it contains:</strong></p>
            <ul style={{ marginTop: '8px', marginBottom: '8px', paddingLeft: '20px' }}>
              <li>Example products with all required columns</li>
              <li>Proper formatting for each field</li>
              <li>Shows data types and acceptable values</li>
            </ul>
            <p><strong>How to use:</strong></p>
            <ul style={{ marginTop: '8px', marginBottom: '8px', paddingLeft: '20px' }}>
              <li>Download the file</li>
              <li>Open it in Excel or a text editor</li>
              <li>Replace sample data with your product information</li>
              <li>Save and upload using "Choose CSV" and "Upload CSV"</li>
            </ul>
            <p><strong>Tip:</strong> Use this as a template to ensure your CSV has the correct format and columns.</p>
          </div>
        }
      />

      <InfoModal
        open={infoOpen === 'export_csv'}
        title="Export CSV"
        onClose={() => setInfoOpen(null)}
        content={
          <div>
            <p><strong>Export CSV</strong> downloads all currently displayed products as a CSV file to your computer.</p>
            <p><strong>What gets exported:</strong></p>
            <ul style={{ marginTop: '8px', marginBottom: '8px', paddingLeft: '20px' }}>
              <li>All products currently shown in the list (respects search filters)</li>
              <li>All product details (ID, name, SKU, price, stock, etc.)</li>
              <li>Data formatted for easy editing in Excel</li>
            </ul>
            <p><strong>Use cases:</strong></p>
            <ul style={{ marginTop: '8px', marginBottom: '8px', paddingLeft: '20px' }}>
              <li>Create backups of your product data</li>
              <li>Edit products in bulk using spreadsheet software</li>
              <li>Share product catalogs with team members</li>
              <li>Integrate with other systems</li>
            </ul>
            <p><strong>Tip:</strong> Use search to filter products before exporting if you only need specific items.</p>
          </div>
        }
      />
    </div>
  );
}
